import { htmlTableToRows, tableToMarkdown } from './tableClipboard'

// Comments are stored as plain text with a small markdown subset (headings,
// lists, quotes, code, tables, bold/italic/strike, links) — compact enough for
// the comment length limit and rendered by components/CommentMarkdown.

interface InlineMarks {
    bold: boolean
    italic: boolean
    strike: boolean
}

interface ConvertState {
    formatted: boolean
}

const SKIPPED_TAGS = new Set(['STYLE', 'SCRIPT', 'HEAD', 'META', 'TITLE', 'LINK', 'IMG', 'svg'])
const BLOCK_TAGS = new Set([
    'P', 'DIV', 'SECTION', 'ARTICLE', 'HEADER', 'FOOTER', 'MAIN', 'ASIDE', 'FIGURE', 'FIGCAPTION',
    'H1', 'H2', 'H3', 'H4', 'H5', 'H6', 'UL', 'OL', 'LI', 'BLOCKQUOTE', 'PRE', 'TABLE', 'HR', 'DL', 'DT', 'DD',
])

const getStyle = (element: Element, property: string) => (
    (element as HTMLElement).style?.getPropertyValue(property).trim().toLowerCase() || ''
)

const isBoldElement = (element: Element) => {
    const weight = getStyle(element, 'font-weight')
    if (weight) return weight === 'bold' || weight === 'bolder' || Number(weight) >= 600
    return element.tagName === 'B' || element.tagName === 'STRONG'
}

const isItalicElement = (element: Element) => (
    element.tagName === 'EM' || element.tagName === 'I' || getStyle(element, 'font-style') === 'italic'
)

const isStrikeElement = (element: Element) => (
    ['S', 'DEL', 'STRIKE'].includes(element.tagName) || getStyle(element, 'text-decoration').includes('line-through')
)

const wrapMarker = (value: string, marker: string) => {
    const [, leading, core, trailing] = value.match(/^(\s*)([\s\S]*?)(\s*)$/) || ['', '', value, '']
    return core ? `${leading}${marker}${core}${marker}${trailing}` : value
}

const toInline = (node: Node, marks: InlineMarks, state: ConvertState): string => {
    if (node.nodeType === Node.TEXT_NODE) {
        return (node.textContent || '').replace(/[\s ]+/g, ' ')
    }
    if (node.nodeType !== Node.ELEMENT_NODE) return ''

    const element = node as Element
    if (SKIPPED_TAGS.has(element.tagName)) return ''
    if (element.tagName === 'BR') return '\n'

    if (element.tagName === 'CODE') {
        const code = (element.textContent || '').replace(/\s+/g, ' ')
        if (!code.trim()) return code
        state.formatted = true
        return `\`${code.replace(/`/g, "'")}\``
    }

    const nextMarks = {
        bold: marks.bold || isBoldElement(element),
        italic: marks.italic || isItalicElement(element),
        strike: marks.strike || isStrikeElement(element),
    }
    let value = Array.from(element.childNodes).map((child) => toInline(child, nextMarks, state)).join('')

    if (element.tagName === 'A') {
        const href = element.getAttribute('href') || ''
        const text = value.replace(/\s+/g, ' ').trim()
        if (/^https?:\/\//i.test(href)) {
            state.formatted = true
            return !text || text === href ? href : `[${text.replace(/[[\]]/g, '')}](${href})`
        }
    }

    if (nextMarks.strike && !marks.strike) value = wrapMarker(value, '~~')
    if (nextMarks.italic && !marks.italic) value = wrapMarker(value, '*')
    if (nextMarks.bold && !marks.bold) value = wrapMarker(value, '**')
    if (value.trim() && (nextMarks.bold !== marks.bold || nextMarks.italic !== marks.italic || nextMarks.strike !== marks.strike)) {
        state.formatted = true
    }

    return value
}

const BLOCK_SELECTOR = Array.from(BLOCK_TAGS).join(',').toLowerCase()

// Google Docs wraps the whole document into an inline <b> — an inline element
// holding blocks is walked as a container so its lists and tables survive.
const isBlockNode = (node: Node) => (
    node.nodeType === Node.ELEMENT_NODE
    && (BLOCK_TAGS.has((node as Element).tagName) || Boolean((node as Element).querySelector(BLOCK_SELECTOR)))
)

const NO_MARKS: InlineMarks = { bold: false, italic: false, strike: false }

const toListLines = (list: Element, depth: number, state: ConvertState): string[] => {
    state.formatted = true
    const isOrdered = list.tagName === 'OL'
    let number = Number(list.getAttribute('start')) || 1
    const lines: string[] = []

    Array.from(list.children).forEach((item) => {
        if (item.tagName === 'UL' || item.tagName === 'OL') {
            lines.push(...toListLines(item, depth + 1, state))
            return
        }

        const childNodes = Array.from(item.childNodes)
        const nestedLists = childNodes.filter((child) => (
            child.nodeType === Node.ELEMENT_NODE && ['UL', 'OL'].includes((child as Element).tagName)
        )) as Element[]
        const content = toBlockLines(childNodes.filter((child) => !nestedLists.includes(child as Element)), state)
            .join(' ')
            .trim()
        // Google Docs keeps nested items in one flat list, marked by aria-level.
        const level = depth + Math.max(0, (Number(item.getAttribute('aria-level')) || 1) - 1)
        const marker = isOrdered ? `${number++}.` : '-'

        if (content) lines.push(`${'  '.repeat(level)}${marker} ${content}`)
        nestedLists.forEach((nestedList) => lines.push(...toListLines(nestedList, level + 1, state)))
    })

    return lines
}

const toBlockLines = (nodes: Node[], state: ConvertState): string[] => {
    const lines: string[] = []
    let inlineBuffer = ''

    const flush = () => {
        inlineBuffer.split('\n').forEach((line) => {
            const trimmedLine = line.trim()
            if (trimmedLine) lines.push(trimmedLine)
        })
        inlineBuffer = ''
    }

    nodes.forEach((node) => {
        if (!isBlockNode(node)) {
            inlineBuffer += toInline(node, NO_MARKS, state)
            return
        }

        flush()
        const element = node as Element
        const tagName = element.tagName

        if (/^H[1-6]$/.test(tagName)) {
            const text = toInline(element, { ...NO_MARKS, bold: true }, state).replace(/\s+/g, ' ').trim()
            if (text) {
                state.formatted = true
                lines.push(`${'#'.repeat(Math.min(Number(tagName[1]), 3))} ${text}`)
            }
        } else if (tagName === 'UL' || tagName === 'OL') {
            lines.push(...toListLines(element, 0, state))
        } else if (tagName === 'LI') {
            const text = toBlockLines(Array.from(element.childNodes), state).join(' ').trim()
            if (text) {
                state.formatted = true
                lines.push(`- ${text}`)
            }
        } else if (tagName === 'BLOCKQUOTE') {
            const quoteLines = toBlockLines(Array.from(element.childNodes), state)
            if (quoteLines.length) {
                state.formatted = true
                lines.push(...quoteLines.map((line) => `> ${line}`))
            }
        } else if (tagName === 'PRE') {
            const code = (element.textContent || '').replace(/\r\n?/g, '\n').replace(/\n+$/, '')
            if (code.trim()) {
                state.formatted = true
                lines.push('```', ...code.replace(/```/g, "'''").split('\n'), '```')
            }
        } else if (tagName === 'TABLE') {
            const rows = htmlTableToRows(element.outerHTML)
            if (rows) {
                state.formatted = true
                lines.push(...tableToMarkdown(rows).split('\n'))
            }
        } else if (tagName === 'HR') {
            state.formatted = true
            lines.push('---')
        } else {
            lines.push(...toBlockLines(Array.from(element.childNodes), state))
        }
    })

    flush()
    return lines
}

// Converts pasted rich text (Google Docs, Notion, web pages, Word) into the
// comment markdown. Returns null when the HTML carries no formatting worth
// keeping, so plain text (e.g. code from an editor) is pasted as is.
export function htmlToCommentMarkdown(html: string): string | null {
    if (!html || typeof DOMParser === 'undefined') return null

    const body = new DOMParser().parseFromString(html, 'text/html').body
    if (!body) return null

    const state: ConvertState = { formatted: false }
    const markdown = toBlockLines(Array.from(body.childNodes), state).join('\n').trim()

    return state.formatted && markdown ? markdown : null
}
